import {
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { Switch } from "@mui/material";
import { useMemo } from "react";
import ActionMenu from "@/components/app/ActionMenu";
import { applyDefaultColumnSettings, getAddressFormat } from "@/utils/utils";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import {
  PAYER_ACTION,
  PAYER_MESSAGES,
  PAYER_SORTABLE_FIELDS,
  PAYER_TYPE_LABEL,
  PayerAction,
  PayerPermissions,
  PayerRow,
} from "../payers-constant";

export interface PayersTableProps {
  rows: PayerRow[];
  rowCount: number;
  isLoading: boolean;
  isArchivedView: boolean;
  permissions: PayerPermissions;
  pagination: GridPaginationModel;
  sortModel: GridSortModel;
  onPaginationChange: (model: GridPaginationModel) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onAction: (action: PayerAction, payer: PayerRow) => void;
  onStatusRequest: (payer: PayerRow) => void;
}

const getActionOptions = (
  payer: PayerRow,
  isArchivedView: boolean,
  permissions: PayerPermissions
) => {
  if (isArchivedView) {
    return permissions.canArchive
      ? [{ value: PAYER_ACTION.RESTORE, label: "Restore" }]
      : [];
  }
  return [
    ...(permissions.canEdit
      ? [{ value: PAYER_ACTION.EDIT, label: "Edit" }]
      : []),
    ...(permissions.canArchive
      ? [
          {
            value: PAYER_ACTION.ARCHIVE,
            label: "Archive",
            isDisabled: !!payer.active,
            hoverMsg: PAYER_MESSAGES.ARCHIVE_DISABLED,
          },
        ]
      : []),
  ];
};

const PayersTable = ({
  rows,
  rowCount,
  isLoading,
  isArchivedView,
  permissions,
  pagination,
  sortModel,
  onPaginationChange,
  onSortModelChange,
  onAction,
  onStatusRequest,
}: PayersTableProps) => {
  const columns = useMemo<GridColDef<PayerRow>[]>(
    () =>
      applyDefaultColumnSettings<PayerRow>(
        [
          {
            field: "payerId",
            headerName: "Payer ID",
            flex: 0.8,
            minWidth: 120,
          },
          {
            field: "payerName",
            headerName: "Payer Name",
            flex: 1.4,
            minWidth: 200,
          },
          {
            field: "payerType",
            headerName: "Payer Type",
            flex: 1,
            minWidth: 160,
            renderCell: ({ row }) =>
              row.payerType ? PAYER_TYPE_LABEL[row.payerType] : "-",
          },
          {
            field: "address",
            headerName: "Address",
            flex: 1.6,
            minWidth: 240,
            renderCell: ({ row }) =>
              getAddressFormat(row.address) || PAYER_MESSAGES.ADDRESS_MISSING,
          },
          {
            field: "active",
            headerName: "Status",
            flex: 0.7,
            minWidth: 110,
            renderCell: ({ row }) => (
              <Switch
                size="small"
                checked={!!row.active}
                disabled={isArchivedView || !permissions.canChangeStatus}
                onChange={() => onStatusRequest(row)}
                inputProps={{ "aria-label": row.payerId }}
              />
            ),
          },
          {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            renderCell: ({ row }) => {
              const options = getActionOptions(
                row,
                isArchivedView,
                permissions
              );
              return options.length > 0 ? (
                <ActionMenu
                  options={options}
                  onActionClick={(action) =>
                    onAction(action as PayerAction, row)
                  }
                />
              ) : null;
            },
          },
        ],
        PAYER_SORTABLE_FIELDS
      ),
    [isArchivedView, permissions, onAction, onStatusRequest]
  );

  return (
    <CustomDataGridV2
      loading={isLoading}
      rows={rows}
      columns={columns}
      getRowId={(row: PayerRow) => row.payerId}
      rowCount={rowCount}
      paginationMode="server"
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      paginationModel={pagination}
      onPaginationModelChange={onPaginationChange}
    />
  );
};

export default PayersTable;
